import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "../components/Head";
import { AUTHOR, DATA_UPDATED, DATA_UPDATED_LABEL, FIRMS_COUNT_LABEL, GALIKIN_URL, SITE_URL, STATS_PERIOD, authorSchema, publisherSchema } from "../content/site";
import "./style/content.css";

const FAQ = [
  ["Odkud FinPilot bere data?",
    "Z účetních závěrek, které české firmy povinně zveřejňují ve Sbírce listin obchodního rejstříku (justice.cz). Data čistíme, sjednocujeme a počítáme z nich vlastní oborové statistiky."],
  ["Kolik firem statistika pokrývá?",
    "Více než 25 000 českých firem s ročním obratem nad 30 milionů korun, roky 2016–2025. Hlavní srovnávací období jsou roky 2023–2025."],
  ["Jak přesné je ocenění firmy?",
    "Ocenění je orientační. Vychází z veřejných účetních výkazů a srovnání s oborem a ukazuje rozpětí hodnoty. Nenahrazuje znalecký posudek ani poradce při prodeji firmy."],
  ["Jak často se data aktualizují?",
    "Statistiku přepočítáváme po zveřejnění nových účetních závěrek, obvykle jednou až dvakrát ročně. Datum poslední aktualizace je uvedeno na stránkách se statistikami."],
];

export default function AboutPage() {
  const url = `${SITE_URL}/o-projektu`;
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "AboutPage", name: "O projektu FinPilot a metodice", url, inLanguage: "cs-CZ", dateModified: DATA_UPDATED, author: authorSchema, publisher: publisherSchema },
      { ...authorSchema, description: `${AUTHOR.role}; ${AUTHOR.past}.` },
      { "@type": "FAQPage", mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
    ],
  };
  return (
    <main className="content-page">
      <Helmet>
        <title>O projektu a metodice: odkud jsou data a kdo za FinPilotem stojí | FinPilot</title>
        <meta name="description" content="FinPilot počítá oborové statistiky a orientační ocenění z účetních závěrek více než 25 000 českých firem. Zdroj dat, postup zpracování, omezení a autor." />
        <meta name="robots" content="index,follow" />
        <meta property="og:title" content="O projektu FinPilot a metodice" />
        <meta property="og:url" content={url} />
        <link rel="canonical" href={url} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <span className="content-kicker">O projektu</span>
      <h1>Odkud jsou data a kdo za FinPilotem stojí</h1>
      <p className="content-lead">
        FinPilot je online nástroj pro finanční analýzu a orientační ocenění českých firem. Z účetních závěrek {FIRMS_COUNT_LABEL} firem
        počítá vlastní oborové statistiky a každou firmu umí porovnat s jejím oborem i s firmami podobné velikosti.
      </p>

      <h2>Zdroj dat</h2>
      <p>
        Vycházíme z účetních závěrek (rozvaha a výkaz zisku a ztráty), které firmy zveřejňují ve Sbírce listin obchodního rejstříku
        na justice.cz. Jde o veřejné údaje. Hodnota FinPilotu je v jejich zpracování: výkazy převádíme do jednotné struktury,
        kontrolujeme a počítáme z nich ukazatele a statistiky, které jinde nenajdete.
      </p>
      <div className="content-stats">
        <div className="content-stat"><div className="content-stat__value">25 000+</div><div className="content-stat__label">firem ve statistice</div></div>
        <div className="content-stat"><div className="content-stat__value">2016–2025</div><div className="content-stat__label">pokryté roky</div></div>
        <div className="content-stat"><div className="content-stat__value">{STATS_PERIOD}</div><div className="content-stat__label">hlavní srovnávací období</div></div>
        <div className="content-stat"><div className="content-stat__value">{DATA_UPDATED_LABEL}</div><div className="content-stat__label">poslední aktualizace</div></div>
      </div>

      <h2>Jak statistiky vznikají</h2>
      <ul>
        <li><strong>Které firmy.</strong> České firmy s ročním obratem nad 30 milionů korun, u kterých jsou k dispozici aspoň tři roky výkazů.</li>
        <li><strong>Kontrola dat.</strong> Sjednocujeme jednotky, hlídáme návaznost rozvahy a vyřazujeme zjevně chybné výkazy.</li>
        <li><strong>Ukazatele.</strong> Rentabilita, marže, likvidita, zadluženost, doby obratu, cyklus peněz, přidaná hodnota a růst. U každého ukazatele je na webu uveden vzorec.</li>
        <li><strong>Statistika.</strong> Pro každý obor uvádíme medián a kvartily, ne průměr: několik výjimečných firem tak výsledek nezkreslí. Extrémní hodnoty omezujeme.</li>
        <li><strong>Obory.</strong> Členění podle klasifikace CZ-NACE na sekce, oddíly a podrobnější skupiny. Obory s méně než 20 firmami označujeme jako orientační, s méně než 5 firmami je nezobrazujeme.</li>
        <li><strong>Růst.</strong> Uvádíme růst typické firmy (medián) i růst součtu oboru, za období 2018–2024 a 2022–2025.</li>
      </ul>

      <h2>Jak funguje ocenění a analýza firmy</h2>
      <p>
        Po nahrání účetní závěrky FinPilot přečte výkazy, spočítá ukazatele a porovná je s oborem a s firmami podobné velikosti.
        Orientační hodnota firmy vychází z provozního zisku, velikosti a stability firmy, čistého majetku a atraktivity oboru.
        Výsledkem je rozpětí, ne jedno číslo, a seznam faktorů, které hodnotu ovlivnily.
      </p>

      <h2>Omezení</h2>
      <ul>
        <li>Závěrky za poslední rok zveřejňují firmy postupně. Rok 2025 zatím pokrývá zhruba polovinu firem.</li>
        <li>Statistika nezahrnuje nejmenší firmy a firmy, které závěrky nezveřejňují.</li>
        <li>Ocenění je orientační a neslouží jako znalecký posudek ani investiční doporučení.</li>
      </ul>

      <h2>Kdo za projektem stojí</h2>
      <p>
        Autorem metodiky, statistik a analytické části je <strong>{AUTHOR.name}</strong>, {AUTHOR.role}. {AUTHOR.past.charAt(0).toUpperCase() + AUTHOR.past.slice(1)}.
        FinPilot vznikal ve spolupráci s poradenskou společností{" "}
        <a href={GALIKIN_URL} target="_blank" rel="noopener noreferrer">Galik In</a>, která se zabývá prodejem a nákupem firem.
      </p>
      <p>
        FinPilot je samostatný projekt autora a není produktem jeho zaměstnavatele. Provozovatel: Ing. Ivan Bazhin, IČO 19556489.
        Kontakt: <a href="mailto:info@finpilot.cz">info@finpilot.cz</a>.
      </p>

      <h2>Časté otázky</h2>
      {FAQ.map(([q, a]) => (
        <div key={q}>
          <h3>{q}</h3>
          <p>{a}</p>
        </div>
      ))}

      <div className="content-cta">
        <p><strong>Podívejte se na data.</strong></p>
        <Link className="content-btn" to="/obory">Přehled oborů</Link>
        <Link className="content-btn" to="/benchmark-obory">Benchmark oborů</Link>
        <Link className="content-btn" to="/blog">Blog</Link>
      </div>
    </main>
  );
}
